import { useEffect } from 'react';

import { getSessionId } from 'state/amplitude/amplitude-package';
import { RunningExperiments, useExperiments } from 'state/experiments';

import {
  clearCloserStoreCoachmarkExposureSessions,
  getExposedCloserStoreCoachmarkVariantForSession,
  hasHandledCloserStoreCoachmarkExposureSession,
  markCloserStoreCoachmarkExposureSessionHandled,
} from './closer-store-coachmark-exposure-sessions';

const CLOSER_STORE_COACHMARK_EXPOSURE_DEDUPE_PREFIX = 'amplitude-session:';

export const getCloserStoreCoachmarkExperimentSessionDedupeKey = (sessionId?: string | null) =>
  sessionId ? `${CLOSER_STORE_COACHMARK_EXPOSURE_DEDUPE_PREFIX}${sessionId}` : undefined;

export const clearCloserStoreCoachmarkExperimentExposureSessionsForTests = () => {
  clearCloserStoreCoachmarkExposureSessions();
};

/**
 * `show-closer-store-coachmark-in-checkout` A/B experiment.
 *
 * Treatment shows the non-blocking closer-restaurant coachmark on pickup checkout; control
 * hides it. Defaults to control when the variant is unresolved so checkout stays safe if
 * Amplitude is slow or fails (per the stability note in running-experiments.ts).
 */
const useCloserStoreCoachmarkVariant = () => {
  const { getVariantValue } = useExperiments();

  return getVariantValue(RunningExperiments.ShowCloserStoreCoachmarkInCheckout)?.value;
};

/**
 * Once this session has an exposed variant locked in (see useExposeCloserStoreCoachmarkExperiment),
 * the gate must keep reading that same locked value for the rest of the session - even if
 * Amplitude later resolves the live variant to something else. Otherwise a late-resolving fetch
 * could flip the rendered coachmark to a different arm than the one already reported as exposed,
 * so the UI a guest actually sees would no longer match their recorded exposure.
 */
export const useEnableCloserStoreCoachmarkExperiment = () => {
  const liveVariantValue = useCloserStoreCoachmarkVariant();
  const amplitudeSessionId = getSessionId()?.toString();
  const dedupeKey = getCloserStoreCoachmarkExperimentSessionDedupeKey(amplitudeSessionId);
  const lockedVariantValue = dedupeKey
    ? getExposedCloserStoreCoachmarkVariantForSession(dedupeKey)
    : undefined;

  return (lockedVariantValue ?? liveVariantValue) === 'treatment';
};

/**
 * Logs experiment exposure once per Amplitude session for both treatment and control, but only
 * for guests who are actually eligible for the coachmark (a closer restaurant exists, plus the
 * structural preconditions: pickup mode, store selected, location permission granted) - for
 * both arms, not just treatment. A control user who would have qualified is exposed the same
 * way a qualifying treatment user is; an ineligible guest in either arm is never exposed.
 *
 * Callers must pass `isEligible: undefined` while eligibility is still pending (e.g. the
 * nearby-store fetch hasn't completed yet) so exposure doesn't fire - or skip - based on a
 * mid-evaluation false negative, and only pass a real `true`/`false` once resolved.
 *
 * Latches per session locally (not just via ExperimentsProvider's dedupeKey) because that
 * provider's own dedupe key includes the resolved variant value - if Amplitude later resolves
 * the same session to a different arm, its dedupe would treat that as a new exposure and log
 * a second $exposure event for one session. The local latch also records which variant was
 * exposed, so useEnableCloserStoreCoachmarkExperiment can lock the rendered UI to that same
 * value instead of drifting to whatever the live variant becomes afterward.
 */
export const useExposeCloserStoreCoachmarkExperiment = (isEligible: boolean | undefined) => {
  const { logExposure } = useExperiments();
  const variantValue = useCloserStoreCoachmarkVariant();
  const amplitudeSessionId = getSessionId()?.toString();

  useEffect(() => {
    const dedupeKey = getCloserStoreCoachmarkExperimentSessionDedupeKey(amplitudeSessionId);

    if (
      !isEligible ||
      !variantValue ||
      !dedupeKey ||
      hasHandledCloserStoreCoachmarkExposureSession(dedupeKey)
    ) {
      return;
    }

    logExposure(RunningExperiments.ShowCloserStoreCoachmarkInCheckout, { dedupeKey });
    markCloserStoreCoachmarkExposureSessionHandled(dedupeKey, variantValue);
  }, [amplitudeSessionId, isEligible, logExposure, variantValue]);
};
